"use client";

import React from "react";
import {
  Box,
  Flex,
  Text,
  HStack,
  Button,
  useToast,
  Select,
  Tabs,
  TabList,
  Tab,
  Spinner,
  VStack,
  useDisclosure,
  Badge,
  Grid,
  Card,
  CardBody,
  CardHeader,
  IconButton,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { DeleteIcon, DownloadIcon, EditIcon } from "@chakra-ui/icons";
import { motion } from "framer-motion";
import { FileText, LinkIcon, BookPlus, Book } from "lucide-react";
import VoiceAgents from "../VoiceAgents";
import VoicePhoneNumbers from "./VoicePhoneNumbers";
import {
  knowledgeBaseService,
  type KnowledgeBase,
} from "../../voice-services/knowledgeBaseService";
import { AddKnowledgeBaseModal } from "../voice-knowledge-base/AddKnowledgeBaseModal";
import { KnowledgeBaseDetailsModal } from "../voice-knowledge-base/KnowledgeBaseDetailsModal";
import { useAuth } from "../../contexts/AuthContext";
import { useStableCredits } from "../../hooks/useStableCredits";

const MotionBox = motion(Box);

const CACHE_TTL_MS = 5 * 60 * 1000;

const VoiceKnowledgeBase = () => {
  const { isAdmin, currentClientId, clients } = useAuth();
  const credits = useStableCredits();

  const [knowledgeBases, setKnowledgeBases] = React.useState<
    KnowledgeBase[] | null
  >(null);
  const [selectedKnowledgeBase, setSelectedKnowledgeBase] =
    React.useState<KnowledgeBase | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [selectedVoiceTab, setSelectedVoiceTab] =
    React.useState<string>("knowledge-base");
  const [selectedAgentType, setSelectedAgentType] = React.useState<string>(
    () => {
      return localStorage.getItem("currentAgentType") || "Voice";
    }
  );

  const {
    isOpen: isAddOpen,
    onOpen: onAddOpen,
    onClose: onAddClose,
  } = useDisclosure();
  const {
    isOpen: isDetailsOpen,
    onOpen: onDetailsOpen,
    onClose: onDetailsClose,
  } = useDisclosure();

  const toast = useToast();
  const navigate = useNavigate();

  // --- tenant + cache helpers (same pattern as VoiceAgents) ---
  const abortControllerRef = React.useRef<AbortController | null>(null);

  const companyName = React.useMemo(() => {
    const currentClient = clients.find((c) => c.id === currentClientId);
    return isAdmin && currentClient ? currentClient.company_name : undefined;
  }, [isAdmin, currentClientId, clients]);

  const getCacheKey = React.useCallback(
    (key: string) => `voice_kb_${key}_${companyName || "self"}`,
    [companyName]
  );

  const loadFromCache = React.useCallback(
    (key: string) => {
      try {
        const raw = localStorage.getItem(getCacheKey(key));
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        const age = Date.now() - (parsed.timestamp || 0);
        if (age > CACHE_TTL_MS) return null;
        return parsed.data ?? null;
      } catch {
        return null;
      }
    },
    [getCacheKey]
  );

  const saveToCache = React.useCallback(
    (key: string, data: any) => {
      try {
        localStorage.setItem(
          getCacheKey(key),
          JSON.stringify({ data, timestamp: Date.now() })
        );
      } catch {}
    },
    [getCacheKey]
  );

  // ✅ On tenant change: abort in-flight, reset UI, hydrate loader state from cache
  React.useEffect(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Clear previous tenant UI state to avoid flashing wrong KBs
    setKnowledgeBases(null);
    setSelectedKnowledgeBase(null);
    setIsLoading(true);

    // Only show big loader if there's no cached KBs for this tenant
    const hasCachedKBs = !!loadFromCache("knowledge_bases");
    setIsLoading(!hasCachedKBs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyName, loadFromCache]);

  const loadKnowledgeBases = React.useCallback(async () => {
    // Abort any in-flight requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    // 1) Hydrate from cache immediately for instant UI
    const cachedKBs = loadFromCache("knowledge_bases");
    if (cachedKBs) {
      setKnowledgeBases(cachedKBs);
      // If cached exists, ensure we don't block the UI
      setIsLoading(false);
    }

    try {
      // 2) Fetch fresh data
      const data = await knowledgeBaseService.list(companyName);

      if (signal.aborted) return;

      setKnowledgeBases(data);
      saveToCache("knowledge_bases", data);
    } catch (error: any) {
      // If aborted, do nothing
      if (error?.name === "AbortError") return;

      console.error("Error loading knowledge bases:", error);
      toast({
        title: "Error loading knowledge bases",
        description:
          error.response?.data?.message || "An unexpected error occurred",
        status: "error",
        duration: 5000,
      });

      // Fall back to empty list (but don't overwrite valid cached UI unless none)
      setKnowledgeBases((prev) => prev ?? []);
    } finally {
      if (!signal.aborted) {
        setIsLoading(false);
      }
    }
  }, [toast, companyName, loadFromCache, saveToCache]);

  React.useEffect(() => {
    loadKnowledgeBases();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [loadKnowledgeBases]);

  // Keep tab persistence behavior EXACT as you had it
  React.useEffect(() => {
    const savedTab =
      localStorage.getItem("currentAgentTab") || "knowledge-base";
    setSelectedVoiceTab(savedTab);

    const handleTabChange = (event: any) => {
      setSelectedVoiceTab(event.detail);
      localStorage.setItem("currentAgentTab", event.detail);
    };

    window.addEventListener("agentTabChange", handleTabChange);
    return () => {
      window.removeEventListener("agentTabChange", handleTabChange);
    };
  }, []);

  const handleTabChange = (newTab: string) => {
    setSelectedVoiceTab(newTab);
    localStorage.setItem("currentAgentTab", newTab);
    window.dispatchEvent(new CustomEvent("agentTabChange", { detail: newTab }));
  };

  const handleAddSuccess = () => {
    loadKnowledgeBases();
    onAddClose();
  };

  const handleDelete = async (knowledgeBaseId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await knowledgeBaseService.delete(knowledgeBaseId);
      toast({
        title: "Knowledge base deleted",
        status: "success",
        duration: 3000,
      });
      loadKnowledgeBases();
    } catch (error: any) {
      toast({
        title: "Error deleting knowledge base",
        description: error.response?.data?.message,
        status: "error",
        duration: 5000,
      });
    }
  };

  const handleCardClick = (kb: KnowledgeBase) => {
    setSelectedKnowledgeBase(kb);
    onDetailsOpen();
  };

  const handleDetailsRefresh = (updated?: KnowledgeBase) => {
    if (updated) {
      setKnowledgeBases(
        (prev) =>
          prev?.map((kb) =>
            kb.knowledge_base_id === updated.knowledge_base_id ? updated : kb
          ) || null
      );
      setSelectedKnowledgeBase(updated);

      // keep cache in sync with updated KB so next visit is instant + correct
      setKnowledgeBases((prev) => {
        const next =
          prev?.map((kb) =>
            kb.knowledge_base_id === updated.knowledge_base_id ? updated : kb
          ) || null;
        if (next) saveToCache("knowledge_bases", next);
        return next;
      });
    }
  };

  const handleAgentTypeChange = (type: string) => {
    setSelectedAgentType(type);
    localStorage.setItem("currentAgentType", type);

    if (type === "All") {
      navigate("/all-agents");
    } else if (type === "Voice") {
      navigate("/voice-agents");
    } else if (type === "SMS") {
      navigate("/sms-agents");
    } else if (type === "Email") {
      navigate("/email-agents");
    } else if (type === "WhatsApp") {
      navigate("/whatsapp-agents");
    }
  };

  const handleDownloadSource = async (
    source: any,
    kbName: string,
    e: React.MouseEvent
  ) => {
    e.stopPropagation();
    try {
      const downloadUrl = source.file_url || source.content_url || source.url;
      if (!downloadUrl) {
        toast({
          title: "No download URL available",
          status: "warning",
          duration: 2000,
        });
        return;
      }

      // Create a temporary anchor element to trigger download
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = source.filename || source.title || "download";
      a.target = "_blank";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      toast({
        title: "Download started",
        status: "success",
        duration: 2000,
      });
    } catch (error) {
      toast({
        title: "Error downloading source",
        status: "error",
        duration: 3000,
      });
    }
  };

  if (selectedAgentType === "Voice") {
    if (selectedVoiceTab === "agents") {
      return <VoiceAgents />;
    }
    if (selectedVoiceTab === "phone-numbers") {
      return <VoicePhoneNumbers />;
    }
  }

  const getTabIndex = () => {
    switch (selectedVoiceTab) {
      case "agents":
        return 0;
      case "phone-numbers":
        return 1;
      case "knowledge-base":
        return 2;
      default:
        return 2;
    }
  };

  const handleTabIndexChange = (index: number) => {
    const tabs = ["agents", "phone-numbers", "knowledge-base"];
    handleTabChange(tabs[index]);
  };

  if (isLoading) {
    return (
      <Flex justify="center" align="center" h="100vh" w="100%">
        <Spinner size="xl" color="primary.500" />
      </Flex>
    );
  }

  const totalSources =
    knowledgeBases?.reduce(
      (sum, kb) => sum + (kb.knowledge_base_sources?.length || 0),
      0
    ) || 0;
  const completedKBs =
    knowledgeBases?.filter((kb) => kb.status === "complete").length || 0;
  const inProgressKBs =
    knowledgeBases?.filter((kb) => kb.status === "in_progress").length || 0;

  return (
    <Box p={6} bg="white" minH="100vh">
      <Flex justify="space-between" align="center" mb={6}>
        <HStack spacing={4}>
          <Tabs
            index={getTabIndex()}
            onChange={handleTabIndexChange}
            variant="solid-rounded"
            colorScheme="blackAlpha"
          >
            <TabList
              bg="gray.100"
              p={1}
              borderRadius="lg"
              border="1px solid"
              borderColor="gray.300"
            >
              <Tab
                fontWeight="bold"
                fontSize="md"
                color="black"
                _selected={{ bg: "white", color: "black", boxShadow: "sm" }}
                _hover={{
                  bg: selectedVoiceTab === "agents" ? "white" : "gray.200",
                }}
              >
                🤖 Voice Agents
              </Tab>
              <Tab
                fontWeight="bold"
                fontSize="md"
                color="black"
                _selected={{ bg: "white", color: "black", boxShadow: "sm" }}
                _hover={{
                  bg:
                    selectedVoiceTab === "phone-numbers" ? "white" : "gray.200",
                }}
              >
                📞 Voice Phone Numbers
              </Tab>
              <Tab
                fontWeight="bold"
                fontSize="md"
                color="black"
                _selected={{ bg: "white", color: "black", boxShadow: "sm" }}
                _hover={{
                  bg:
                    selectedVoiceTab === "knowledge-base"
                      ? "white"
                      : "gray.200",
                }}
              >
                📚 Knowledge Base
              </Tab>
            </TabList>
          </Tabs>
        </HStack>

        <HStack spacing={2}>
          <MotionBox
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            transition="0.15s ease"
          >
            <Select
              value={selectedAgentType}
              onChange={(e) => handleAgentTypeChange(e.target.value)}
              height="48px"
              bg="white"
              border="2px solid"
              borderColor="gray.200"
              borderRadius="12px"
              color="gray.800"
              fontWeight="semibold"
              fontSize="sm"
              pl="12px"
              pr="36px"
              cursor="pointer"
              shadow="sm"
              transition="all 0.2s ease"
              _hover={{
                borderColor: "primary.400",
                shadow: "md",
              }}
              _focus={{
                borderColor: "primary.600",
                boxShadow: "0 0 0 3px rgba(99, 102, 241, 0.15)",
              }}
            >
              <option value="All">🤖 All Agents</option>
              <option value="Voice">📞 Voice Agents</option>
              <option value="SMS">💬 SMS Agents</option>

              {process.env.REACT_APP_IS_MAIN_APP === "true" && (
                <option value="Email">📧 Email Agents</option>
              )}

              <option value="WhatsApp">📱 WhatsApp Agents</option>
            </Select>
          </MotionBox>
          <Button
            onClick={onAddOpen}
            variant="brandGradient"
            fontWeight="medium"
            leftIcon={<BookPlus size={16} />}
            isDisabled={credits !== null && credits < 0.01}
            title={
              credits !== null && credits < 0.01
                ? "Insufficient balance to create knowledge base"
                : undefined
            }
          >
            Create Knowledge Base
          </Button>
        </HStack>
      </Flex>

      {knowledgeBases && knowledgeBases.length > 0 && (
        <Grid templateColumns="repeat(4, 1fr)" gap={4} mb={6}>
          <Card
            transition="all 0.3s ease"
            _hover={{
              transform: "translateY(-4px) scale(1.02)",
              shadow: "xl",
            }}
          >
            <CardBody>
              <VStack align="start" spacing={1}>
                <Text fontSize="sm" color="gray.600">
                  Total Knowledge Bases
                </Text>
                <Text fontSize="2xl" fontWeight="bold" color="black">
                  {knowledgeBases?.length || 0}
                </Text>
              </VStack>
            </CardBody>
          </Card>
          <Card
            transition="all 0.3s ease"
            _hover={{
              transform: "translateY(-4px) scale(1.02)",
              shadow: "xl",
            }}
          >
            <CardBody>
              <VStack align="start" spacing={1}>
                <Text fontSize="sm" color="gray.600">
                  Completed
                </Text>
                <Text fontSize="2xl" fontWeight="bold" color="green.500">
                  {completedKBs}
                </Text>
              </VStack>
            </CardBody>
          </Card>
          <Card
            transition="all 0.3s ease"
            _hover={{
              transform: "translateY(-4px) scale(1.02)",
              shadow: "xl",
            }}
          >
            <CardBody>
              <VStack align="start" spacing={1}>
                <Text fontSize="sm" color="gray.600">
                  In Progress
                </Text>
                <Text fontSize="2xl" fontWeight="bold" color="yellow.500">
                  {inProgressKBs}
                </Text>
              </VStack>
            </CardBody>
          </Card>
          <Card
            transition="all 0.3s ease"
            _hover={{
              transform: "translateY(-4px) scale(1.02)",
              shadow: "xl",
            }}
          >
            <CardBody>
              <VStack align="start" spacing={1}>
                <Text fontSize="sm" color="gray.600">
                  Total Sources
                </Text>
                <Text fontSize="2xl" fontWeight="bold" color="blue.500">
                  {totalSources}
                </Text>
              </VStack>
            </CardBody>
          </Card>
        </Grid>
      )}

      {!knowledgeBases || knowledgeBases.length === 0 ? (
        <Flex
          direction="column"
          align="center"
          justify="center"
          h="500px"
          minH="calc(100vh - 80px)"
          bg="gray.50"
          borderRadius="md"
          border="1px solid"
          borderColor="gray.200"
        >
          <Book size={48} color="gray" />
          <Text fontSize="lg" fontWeight="medium" color="gray.600" mt={4}>
            No Knowledge Bases Found!
          </Text>
          <Text fontSize="sm" color="gray.500" mt={2}>
            Create a knowledge base to get started.
          </Text>
          <Button
            onClick={onAddOpen}
            mt={6}
            colorScheme="primary"
            bg="primary.800"
            _hover={{ bg: "primary.900" }}
            leftIcon={<BookPlus size={16} />}
            isDisabled={credits !== null && credits < 0.01}
            title={
              credits !== null && credits < 0.01
                ? "Insufficient balance to create knowledge base"
                : undefined
            }
          >
            Create Knowledge Base
          </Button>
        </Flex>
      ) : (
        <Grid templateColumns="repeat(auto-fit, minmax(400px, 1fr))" gap={6}>
          {knowledgeBases.map((kb) => {
            const sources = kb.knowledge_base_sources || [];
            const documentCount = sources.filter(
              (s) => s.type === "document"
            ).length;
            const textCount = sources.filter((s) => s.type === "text").length;
            const urlCount = sources.filter((s) => s.type === "url").length;

            return (
              <Card
                key={kb.knowledge_base_id}
                borderLeft="4px"
                borderLeftColor="primary.500"
                cursor={
                  credits !== null && credits < 0.01 ? "not-allowed" : "pointer"
                }
                onClick={() => {
                  if (credits !== null && credits < 0.01) return;
                  handleCardClick(kb);
                }}
                _hover={credits !== null && credits < 0.01 ? {} : { shadow: "md" }}
                height="250px"
                display="flex"
                flexDirection="column"
                opacity={credits !== null && credits < 0.01 ? 0.5 : 1}
                title={
                  credits !== null && credits < 0.01
                    ? "Insufficient balance to view knowledge base details"
                    : undefined
                }
              >
                <CardHeader flexShrink={0}>
                  <Flex justify="space-between" align="center">
                    <VStack align="start" spacing={1}>
                      <HStack>
                        <Text fontWeight="bold" fontSize="lg" color="black">
                          {kb.knowledge_base_name}
                        </Text>
                        <Badge
                          colorScheme={
                            kb.status === "complete"
                              ? "green"
                              : kb.status === "error"
                              ? "red"
                              : "yellow"
                          }
                        >
                          {kb.status}
                        </Badge>
                      </HStack>
                    </VStack>
                    <HStack onClick={(e) => e.stopPropagation()}>
                      <IconButton
                        aria-label="Edit knowledge base"
                        icon={<EditIcon />}
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCardClick(kb);
                        }}
                        isDisabled={credits !== null && credits < 0.01}
                        title={
                          credits !== null && credits < 0.01
                            ? "Insufficient balance to edit knowledge base"
                            : undefined
                        }
                      />
                      <IconButton
                        aria-label="Delete knowledge base"
                        icon={<DeleteIcon />}
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        onClick={(e) => handleDelete(kb.knowledge_base_id, e)}
                      />
                    </HStack>
                  </Flex>
                </CardHeader>
                <CardBody pt={0} overflowY="auto" flex="1">
                  <VStack spacing={3} align="stretch">
                    <Grid templateColumns="repeat(3, 1fr)" gap={4}>
                      <Box>
                        <HStack spacing={1}>
                          <FileText size={14} color="gray" />
                          <Text fontSize="sm" color="gray.600">
                            Documents
                          </Text>
                        </HStack>
                        <Text fontWeight="bold" color="black">
                          {documentCount}
                        </Text>
                      </Box>
                      <Box>
                        <HStack spacing={1}>
                          <FileText size={14} color="gray" />
                          <Text fontSize="sm" color="gray.600">
                            Texts
                          </Text>
                        </HStack>
                        <Text fontWeight="bold" color="black">
                          {textCount}
                        </Text>
                      </Box>
                      <Box>
                        <HStack spacing={1}>
                          <LinkIcon size={14} color="gray" />
                          <Text fontSize="sm" color="gray.600">
                            URLs
                          </Text>
                        </HStack>
                        <Text fontWeight="bold" color="black">
                          {urlCount}
                        </Text>
                      </Box>
                    </Grid>

                    <Box bg="gray.50" p={3} borderRadius="md">
                      <Text
                        fontSize="sm"
                        fontWeight="semibold"
                        color="gray.700"
                        mb={2}
                      >
                        Sources ({sources.length}):
                      </Text>
                      {sources.length > 0 ? (
                        <VStack align="stretch" spacing={2}>
                          {sources.map((source, idx) => (
                            <Flex
                              key={idx}
                              justify="space-between"
                              align="center"
                              p={2}
                              bg="white"
                              borderRadius="md"
                              border="1px solid"
                              borderColor="gray.200"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <VStack
                                align="start"
                                spacing={0}
                                flex="1"
                                minW={0}
                              >
                                <Text
                                  fontSize="sm"
                                  fontWeight="medium"
                                  color="black"
                                  noOfLines={1}
                                >
                                  {source.filename ||
                                    source.title ||
                                    source.url ||
                                    "Untitled"}
                                </Text>
                                <Text fontSize="xs" color="gray.500">
                                  {source.type}
                                </Text>
                              </VStack>
                              <IconButton
                                aria-label="Download source"
                                icon={<DownloadIcon />}
                                size="sm"
                                variant="ghost"
                                colorScheme="primary"
                                onClick={(e) =>
                                  handleDownloadSource(
                                    source,
                                    kb.knowledge_base_name,
                                    e
                                  )
                                }
                                flexShrink={0}
                              />
                            </Flex>
                          ))}
                        </VStack>
                      ) : (
                        <Text fontSize="sm" color="gray.500">
                          No sources added yet
                        </Text>
                      )}
                    </Box>

                    {kb.last_refreshed_timestamp && (
                      <Text fontSize="xs" color="gray.600" textAlign="center">
                        Last updated:{" "}
                        {new Date(
                          kb.last_refreshed_timestamp
                        ).toLocaleDateString("en-US", {
                          month: "2-digit",
                          day: "2-digit",
                          year: "numeric",
                        })}
                      </Text>
                    )}
                  </VStack>
                </CardBody>
              </Card>
            );
          })}
        </Grid>
      )}

      <AddKnowledgeBaseModal
        isOpen={isAddOpen}
        onClose={onAddClose}
        onSuccess={handleAddSuccess}
      />
      <KnowledgeBaseDetailsModal
        isOpen={isDetailsOpen}
        onClose={onDetailsClose}
        knowledgeBase={selectedKnowledgeBase}
        onRefresh={handleDetailsRefresh}
      />
    </Box>
  );
};

export default VoiceKnowledgeBase;
